import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { Button, Typography } from '@material-ui/core';

import { getProducePriceRules, updateProducePriceRules } from '../../api';
import { checkStatus, parseJSON, handleError } from '../../../shared/api/core/common';
import { SVG_ICONS } from '../../../shared/icons/SvgIcons';
import { ErrorStatusBar, CustomTableTitle } from '../../../shared/components';
import { Toast, enqueueSnackbar, ToastSuccess, ToastError } from '../../../shared/components/Notifier';
import { ERROR_MSG } from '../../../shared/lib/Localization';
import { hasPermission } from '../../../shared/lib/PermissionManager';
import { Permission } from '../../../shared/models';
import {
  ProduceProductRules,
  ProductKind,
  ProductNumberField,
  newProductOverride,
} from '../../components/ProduceProductRules/ProduceProductRules.web';
import { RuleError, productRuleErrors } from '../../lib/producePriceRules';

export type CostForm = 'flat' | 'percent';
export type CostBasis = 'unit' | 'case';

export type CostComponent = {
  amount: number;
  form: CostForm;
  basis: CostBasis;
};

export type CostSet = {
  transportation: CostComponent;
  waste: CostComponent;
  other: CostComponent;
};

type ProductOverrideCommon = {
  productNumber: string;
  windowDays: number | null;
  fixedPrice: number | null;
  floor: number | null;
  ceiling: number | null;
  transportation: CostComponent | null;
  waste: CostComponent | null;
  other: CostComponent | null;
};

export type ProductOverride =
  | (ProductOverrideCommon & { kind: 'loose' })
  | (ProductOverrideCommon & { kind: 'case'; caseAddOn: number | null });

export type ProducePriceRules = {
  businessId: string;
  windowDays: number;
  caseAddOn: number;
  looseCosts: CostSet;
  caseCosts: CostSet;
  products: ProductOverride[];
};

export type RulesSaveResult =
  | { saved: true; rules: ProducePriceRules }
  | {
    saved: false;
    errors: Array<{ productNumber: string | null; field: 'fixedPrice' | 'floor' | 'ceiling'; message: string }>;
  };

type CostGroup = 'looseCosts' | 'caseCosts';
type CostName = keyof CostSet;

const SET_PRODUCE_FIXED_PRICE_PERMISSION = 'set_produce_fixed_price';

const COST_FORMS: CostForm[] = ['flat', 'percent'];
const COST_BASES: CostBasis[] = ['unit', 'case'];
const COST_NAMES: CostName[] = ['transportation', 'waste', 'other'];
const COST_GROUPS: Array<{ key: CostGroup; title: string; rowPrefix: string }> = [
  { key: 'looseCosts', title: 'Loose produce costs', rowPrefix: 'Loose' },
  { key: 'caseCosts', title: 'Case produce costs', rowPrefix: 'Case' },
];

const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 };

const CostRow = (props: {
  label: string;
  cost: CostComponent;
  onChange: (next: CostComponent) => void;
}) => {
  const { label, cost, onChange } = props;
  return (
    <div style={rowStyle}>
      <span style={{ minWidth: 200 }}>{label}</span>
      <input
        type="number"
        aria-label={`${label} amount`}
        value={cost.amount}
        onChange={(event) => onChange({ ...cost, amount: Number(event.target.value) })}
        style={{ width: 88 }}
      />
      <select
        aria-label={`${label} form`}
        value={cost.form}
        onChange={(event) => onChange({ ...cost, form: event.target.value as CostForm })}
      >
        {COST_FORMS.map((form) => <option key={form} value={form}>{form}</option>)}
      </select>
      <select
        aria-label={`${label} basis`}
        value={cost.basis}
        onChange={(event) => onChange({ ...cost, basis: event.target.value as CostBasis })}
      >
        {COST_BASES.map((basis) => <option key={basis} value={basis}>per {basis}</option>)}
      </select>
    </div>
  );
};

export const ProducePriceRulesScreen = (props: any) => {
  const [isErrorStatusVisible, setErrorStatusVisible] = useState(false);
  const [rules, setRules] = useState<ProducePriceRules | null>(null);
  const [ruleErrors, setRuleErrors] = useState<RuleError[]>([]);
  const businessId = props.userData && props.userData.selectedBusiness && props.userData.selectedBusiness.business_id;
  const authUser = props.userData?.userData?.userData;
  const isSuperUser: boolean = authUser?.is_superuser === true;
  const userPermissions: Permission[] = authUser?.user_permissions || [];
  const canSetFixedPrice = hasPermission(userPermissions, SET_PRODUCE_FIXED_PRICE_PERMISSION, isSuperUser) === true;

  useEffect(() => {
    if (!businessId) {
      setRules(null);
      return undefined;
    }
    let cancelled = false;
    Promise.resolve(getProducePriceRules(businessId))
      .catch(handleError)
      .then(checkStatus)
      .then(parseJSON)
      .then((data: ProducePriceRules) => {
        if (cancelled) {
          return;
        }
        setRules(data);
        setErrorStatusVisible(false);
      })
      .catch((error: any) => {
        if (cancelled) {
          return;
        }
        setErrorStatusVisible(!!(error && (error.status === 403 || error.status === 401 || error.status === 500)));
      });
    return () => {
      cancelled = true;
    };
  }, [businessId]);

  const changeNumber = (field: 'windowDays' | 'caseAddOn', value: string) => {
    setRules((current) => (current ? { ...current, [field]: Number(value) } : current));
  };

  const changeCost = (group: CostGroup, name: CostName, next: CostComponent) => {
    setRules((current) => (current ? { ...current, [group]: { ...current[group], [name]: next } } : current));
  };

  const changeProduct = (productNumber: string, field: ProductNumberField, value: number | null) => {
    setRules((current) => (current ? {
      ...current,
      products: current.products.map((product) => {
        if (product.productNumber !== productNumber || (field === 'caseAddOn' && product.kind !== 'case')) {
          return product;
        }
        return { ...product, [field]: value };
      }),
    } : current));
  };

  const addProduct = (productNumber: string, kind: ProductKind) => {
    setRules((current) => {
      if (!current || current.products.some((product) => product.productNumber === productNumber)) {
        return current;
      }
      return { ...current, products: [...current.products, newProductOverride(productNumber, kind)] };
    });
  };

  const saveRules = () => {
    if (!rules) {
      return Promise.resolve();
    }
    const localErrors = productRuleErrors(rules.products || []);
    if (localErrors.length > 0) {
      setRuleErrors(localErrors);
      return Promise.resolve();
    }
    setRuleErrors([]);
    return Promise.resolve(updateProducePriceRules(rules))
      .then(async (response: { status: number; json: () => Promise<RulesSaveResult> } | null) => {
        const body = response && response.status === 200 ? await response.json() : null;
        if (body && body.saved === false) {
          setRuleErrors(body.errors || []);
          return;
        }
        if (!body || body.saved !== true) {
          throw body;
        }
        setRules(body.rules);
        props.showToast({ message: 'Produce price rules saved', options: ToastSuccess });
      })
      .catch(() => {
        props.showToast({ message: 'Could not save produce price rules', options: ToastError });
      });
  };

  return (
    <div>
      <ErrorStatusBar isVisible={isErrorStatusVisible} />
      <CustomTableTitle
        history={props.history}
        title={'Produce price rules'}
        icon={SVG_ICONS.reports}
      />
      {isErrorStatusVisible ? <Typography variant="body2">{ERROR_MSG}</Typography> : null}
      {rules ? (
        <div style={{ padding: '8px 12px' }}>
          <div style={rowStyle}>
            <span style={{ minWidth: 200 }}>Price window (days)</span>
            <input
              type="number"
              aria-label="Price window days"
              value={rules.windowDays}
              onChange={(event) => changeNumber('windowDays', event.target.value)}
              style={{ width: 88 }}
            />
          </div>
          <div style={rowStyle}>
            <span style={{ minWidth: 200 }}>Case add-on ($)</span>
            <input
              type="number"
              aria-label="Case add-on"
              value={rules.caseAddOn}
              onChange={(event) => changeNumber('caseAddOn', event.target.value)}
              style={{ width: 88 }}
            />
          </div>
          {COST_GROUPS.map((group) => (
            <div key={group.key} style={{ marginTop: 12 }}>
              <Typography variant="subtitle2">{group.title}</Typography>
              {COST_NAMES.map((name) => (
                <CostRow
                  key={name}
                  label={`${group.rowPrefix} ${name}`}
                  cost={rules[group.key][name]}
                  onChange={(next) => changeCost(group.key, name, next)}
                />
              ))}
            </div>
          ))}
          <ProduceProductRules
            products={rules.products || []}
            canSetFixedPrice={canSetFixedPrice}
            onChangeProduct={changeProduct}
            onAddProduct={addProduct}
          />
          {ruleErrors.length > 0 ? (
            <ul aria-label="Rule errors" style={{ marginTop: 12, color: '#c62828' }}>
              {ruleErrors.map((error) => (
                <li key={`${error.productNumber}-${error.field}-${error.message}`}>
                  {error.productNumber ? `${error.productNumber}: ${error.message}` : error.message}
                </li>
              ))}
            </ul>
          ) : null}
          <div style={{ marginTop: 12 }}>
            <Button variant="contained" color="primary" onClick={saveRules}>Save</Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    userData: state.userLocalData,
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    showToast: (toast: Toast) => dispatch(enqueueSnackbar(toast)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ProducePriceRulesScreen);
